import React, { useState, useEffect } from 'react';
import { api } from '../App';
import '../styles/DashboardPage.css';
import '../styles/PageHero.css';
import AdminBestellingenPage from './AdminBestellingenPage';
import AdminBestellingenBySpeltakPage from './AdminBestellingenBySpeltakPage';
import AdminBestellingDetailsPage from './AdminBestellingDetailsPage';
import AdminCummulatieveLijstPage from './AdminCummulatieveLijstPage';
import AdminProductenPage from './AdminProductenPage';
import AdminLeveringsdatumsPage from './AdminLeveringsdatumsPage';
import AdminDashboardOverzichtPage from './AdminDashboardOverzichtPage';
import AdminStatistiekenPage from './AdminStatistiekenPage';

function parseAdminHash() {
  const hash = window.location.hash.slice(1);
  if (!hash) return { page: 'menu', speltak: null, order: null };
  if (hash === 'bestellingen') return { page: 'bestellingen', speltak: null, order: null };
  if (hash === 'cummulatief') return { page: 'cummulatief', speltak: null, order: null };
  if (hash === 'producten') return { page: 'producten', speltak: null, order: null };
  if (hash === 'leveringsdatums') return { page: 'leveringsdatums', speltak: null, order: null };
  if (hash === 'overzicht') return { page: 'overzicht', speltak: null, order: null };
  if (hash === 'statistieken') return { page: 'statistieken', speltak: null, order: null };
  if (hash.startsWith('speltak-')) {
    const parts = hash.split('-');
    const speltakId = parseInt(parts[1], 10);
    const speltakNaam = decodeURIComponent(parts.slice(2).join('-') || '');
    return {
      page: 'speltak',
      speltak: speltakId ? { id: speltakId, naam: speltakNaam || `Speltak ${speltakId}` } : null,
      order: null
    };
  }
  if (hash.startsWith('details-')) {
    const parts = hash.split('-');
    const orderId = parseInt(parts[1], 10);
    const speltakNaam = decodeURIComponent(parts.slice(2).join('-') || '');
    return {
      page: 'details',
      speltak: null,
      order: orderId ? { id: orderId, speltak_naam: speltakNaam || 'Speltak' } : null
    };
  }
  return { page: 'menu', speltak: null, order: null };
}

function DashboardPage({ user, onLogout }) {
  const initial = parseAdminHash();
  const [currentPage, setCurrentPage] = useState(initial.page);
  const [selectedSpeltak, setSelectedSpeltak] = useState(initial.speltak);
  const [selectedOrder, setSelectedOrder] = useState(initial.order);
  const [speltakBackPage, setSpeltakBackPage] = useState('bestellingen');

  useEffect(() => {
    const onHashChange = () => {
      const parsed = parseAdminHash();
      setCurrentPage(parsed.page);
      setSelectedSpeltak(parsed.speltak);
      setSelectedOrder(parsed.order);
    };

    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, []);

  useEffect(() => {
    if (currentPage === 'menu') {
      if (window.location.hash) {
        window.history.replaceState(null, '', window.location.pathname + window.location.search);
      }
      return;
    }

    let hash = currentPage;
    if (currentPage === 'speltak' && selectedSpeltak) {
      hash = `speltak-${selectedSpeltak.id}-${encodeURIComponent(selectedSpeltak.naam)}`;
    } else if (currentPage === 'details' && selectedOrder) {
      hash = `details-${selectedOrder.id}-${encodeURIComponent(selectedOrder.speltak_naam)}`;
    }

    const newHash = `#${hash}`;
    if (window.location.hash !== newHash) {
      window.location.hash = hash;
    }
  }, [currentPage, selectedSpeltak, selectedOrder]);

  const handleLogout = async () => {
    try {
      await api.post('/index.php?action=logout');
      onLogout();
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  if (currentPage === 'bestellingen') {
    return (
      <AdminBestellingenPage
        user={user}
        onBack={() => setCurrentPage('menu')}
        onLogout={handleLogout}
        onSelectSpeltak={(speltak_id, speltak_naam) => {
          setSpeltakBackPage('bestellingen');
          setSelectedSpeltak({ id: speltak_id, naam: speltak_naam });
          setCurrentPage('speltak');
        }}
      />
    );
  }

  if (currentPage === 'speltak' && selectedSpeltak) {
    return (
      <AdminBestellingenBySpeltakPage
        user={user}
        speltak_id={selectedSpeltak.id}
        speltak_naam={selectedSpeltak.naam}
        onBack={() => setCurrentPage(speltakBackPage)}
        onLogout={handleLogout}
        onViewOrder={(orderId, speltak_naam) => {
          setSelectedOrder({ id: orderId, speltak_naam: speltak_naam });
          setCurrentPage('details');
        }}
      />
    );
  }

  if (currentPage === 'details' && selectedOrder) {
    return (
      <AdminBestellingDetailsPage
        user={user}
        bestellingId={selectedOrder.id}
        speltak_naam={selectedOrder.speltak_naam}
        onBack={() => setCurrentPage('speltak')}
        onLogout={handleLogout}
      />
    );
  }

  if (currentPage === 'cummulatief') {
    return (
      <AdminCummulatieveLijstPage
        user={user}
        onBack={() => setCurrentPage('menu')}
        onLogout={handleLogout}
      />
    );
  }

  if (currentPage === 'producten') {
    return (
      <AdminProductenPage
        onBack={() => setCurrentPage('menu')}
        onLogout={handleLogout}
      />
    );
  }

  if (currentPage === 'leveringsdatums') {
    return (
      <AdminLeveringsdatumsPage
        onBack={() => setCurrentPage('menu')}
        onLogout={handleLogout}
      />
    );
  }

  if (currentPage === 'overzicht') {
    return (
      <AdminDashboardOverzichtPage
        user={user}
        onBack={() => setCurrentPage('menu')}
        onLogout={handleLogout}
        onSelectSpeltak={(speltak_id, speltak_naam) => {
          setSpeltakBackPage('overzicht');
          setSelectedSpeltak({ id: speltak_id, naam: speltak_naam });
          setCurrentPage('speltak');
        }}
      />
    );
  }

  if (currentPage === 'statistieken') {
    return (
      <AdminStatistiekenPage
        onBack={() => setCurrentPage('menu')}
        onLogout={handleLogout}
      />
    );
  }

  return (
    <div className="dashboard-container">
      <div
        className="page-hero"
        style={{ backgroundImage: "url('/images/login-hero.jpg')" }}
      >
        <div className="hero-overlay" />
        <div className="hero-content dashboard-hero-text dashboard-hero-text--admin">
          <h2>Welkom in het Dashboard</h2>
          <p>Beheer je kampboodschappen hier</p>
        </div>
      </div>

      <header className="portal-subheader">
        <div className="header-content">
          <img src="/images/jmc-logo.png" alt="JMC Logo" className="logo-header" />
          <div className="header-title">
            <h1>Kampboodschappen Admin</h1>
            <p>Beheerdersportal</p>
          </div>
        </div>
        <div className="user-info">
          <span className="user-name">{user?.inlognaam}</span>
          <span className="user-role">({user?.rol})</span>
          <button type="button" onClick={handleLogout} className="logout-button">
            Uitloggen
          </button>
        </div>
      </header>

      <main className="dashboard-main">
        <div className="dashboard-content">
          <div className="content-card welcome-card">
            <div className="card-header">
              <h3>Hallo, {user?.inlognaam}! 👋</h3>
            </div>
            <p>Je bent succesvol ingelogd als <strong>{user?.rol}</strong>.</p>
            <p>Via dit dashboard kun je alle kampboodschappen beheren en zien welke bestellingen zijn geplaatst.</p>
          </div>

          <div className="cards-grid">
            <div
              className="feature-card"
              onClick={() => setCurrentPage('overzicht')}
              style={{ cursor: 'pointer' }}
            >
              <div className="card-icon">📊</div>
              <h4>Dashboard-overzicht</h4>
              <p>Per leveringsdatum: wie besteld heeft en hoeveel regels</p>
            </div>
            <div
              className="feature-card"
              onClick={() => setCurrentPage('bestellingen')}
              style={{ cursor: 'pointer' }}
            >
              <div className="card-icon">📋</div>
              <h4>Bestellingen</h4>
              <p>Bekijk en beheer alle bestellingen</p>
            </div>
            <div
              className="feature-card"
              onClick={() => setCurrentPage('producten')}
              style={{ cursor: 'pointer' }}
            >
              <div className="card-icon">📦</div>
              <h4>Producten</h4>
              <p>Beheer je productcatalogus</p>
            </div>
            <div
              className="feature-card"
              onClick={() => setCurrentPage('leveringsdatums')}
              style={{ cursor: 'pointer' }}
            >
              <div className="card-icon">📅</div>
              <h4>Leveringsdatums</h4>
              <p>Beheer beschikbare leveringsdatums</p>
            </div>
            <div
              className="feature-card"
              onClick={() => setCurrentPage('statistieken')}
              style={{ cursor: 'pointer' }}
            >
              <div className="card-icon">📈</div>
              <h4>Statistieken</h4>
              <p>Leuke weetjes: top producten, speltakken en drukste dagen</p>
            </div>
            <div
              className="feature-card"
              onClick={() => setCurrentPage('cummulatief')}
              style={{ cursor: 'pointer' }}
            >
              <div className="card-icon">🛒</div>
              <h4>Boodschappenlijst</h4>
              <p>Cumulatieve lijst per leveringsdatum</p>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}

export default DashboardPage;
